/**
 * useMessagesBadge — seeds + subscribes the header Messages icon badge
 * (messaging-inbox IA WS5). One cheap fetch of
 * GET /api/professional/threads/unread-count on first header mount (dedupe on
 * "loaded", StrictMode-safe), then live updates via useMessagesStore — the
 * Messages inbox re-syncs the store as conversations are read.
 */
import { useEffect, useRef } from "react";
import { getJSON } from "../lib/api";
import { FIXTURE_CONVERSATIONS } from "../lib/fixtures";
import { IS_PREVIEW } from "../lib/pro-mode";
import { useMessagesStore } from "../stores/messages";
import { useProToken } from "./useProToken";

interface UnreadCountResponse {
  unread_count: number;
}

export function useMessagesBadge(): number | null {
  const getToken = useProToken();
  const unreadCount = useMessagesStore((s) => s.unreadCount);
  const loadedRef = useRef(false);

  useEffect(() => {
    // Dedupe on "loaded", not "started" — StrictMode's double-mount fires
    // mount → unmount → remount, and gating on a started flag would leave the
    // second mount with a permanently-unseeded badge if the first mount's
    // fetch was aborted (the documented prefetch-hook lesson).
    if (loadedRef.current || useMessagesStore.getState().unreadCount !== null) {
      return;
    }
    void (async () => {
      try {
        if (IS_PREVIEW) {
          const unread = FIXTURE_CONVERSATIONS.filter(
            (c) => c.unreadCount > 0,
          ).length;
          useMessagesStore.getState().setUnreadCount(unread);
          loadedRef.current = true;
          return;
        }
        const token = (await getToken()) ?? undefined;
        const res = await getJSON<UnreadCountResponse>(
          "/api/professional/threads/unread-count",
          token,
        );
        useMessagesStore.getState().setUnreadCount(res.unread_count);
        loadedRef.current = true;
      } catch {
        // Badge is cosmetic — fail silently; the inbox page re-syncs it.
      }
    })();
  }, [getToken]);

  return unreadCount;
}
